import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@attmos/flowstack-ui";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

import { useProjects } from "@/api/projects";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";

/**
 * Analysis surface, step 1 — pick a project (flowstack VIZ U4.1, the
 * app-level half of D71(iii)). A per-run Analysis tab shipped at #922; this
 * is the top-level `/analysis` entry point the operator asked for
 * (2026-08-13): "user can see his projects in the analysis tab... user
 * select and see the analysis of it selected projects that ran."
 *
 * Deliberately project-first, not run-first: a user arriving here does not
 * have a run id and does not think in one — the project is the handle they
 * think in. `/analysis/:slug` (AnalysisProjectRuns) is the next step.
 *
 * Not a fork of `ProjectListPage` — that page's cards are heavier (3 stat
 * tiles) than what picking a project for its ANALYSES needs (just: does it
 * have runs at all). Kept separate rather than parameterizing the existing
 * page, since the two surfaces' cards are likely to diverge further as
 * U2.1 (cross-run overlay) lands on top of this one.
 */
export function AnalysisProjectPickerPage(): JSX.Element {
  const { data: projects, isLoading, isError, error } = useProjects();

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Analysis" description="Loading…" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Analysis" />
        <EmptyState
          title="Could not load projects."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title="Analysis" />
        <EmptyState
          title="No projects yet."
          description="Pilot tenants are seeded with one project by default. Contact your FlowStack administrator if you don't see one here."
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title="Analysis" description="Pick a project to see the analyses of its runs." />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((project) => (
          <Link
            key={project.project_id}
            to={`/analysis/${project.slug}`}
            className="group block rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            <Card
              elevation="raised"
              className="h-full transition-colors group-hover:border-cyan/40 group-hover:bg-surface-2"
            >
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="font-display text-lg">{project.display_name}</CardTitle>
                  <ArrowRight
                    className="h-4 w-4 -translate-x-1 text-muted-foreground opacity-0 transition-all group-hover:translate-x-0 group-hover:text-cyan group-hover:opacity-100"
                    aria-hidden
                  />
                </div>
                <CardDescription className="font-mono text-xs">{project.slug}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">
                  {project.run_count === 0
                    ? "No runs yet."
                    : project.run_count === 1
                      ? "1 run."
                      : `${project.run_count} runs.`}
                </p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
